/**
 * LiveKit participant → `MediaParticipant`.
 *
 * The SFU identity IS the participant id (`meeting_participants.sfu_identity`),
 * so the REST roster and the media plane key on the same string. Anything the
 * SFU cannot know — avatar, org role, display name — is merged in from the seed
 * roster; anything live — camera, mic, speaking, quality — comes from LiveKit.
 */
import { ConnectionQuality as LiveKitQuality, type Participant, type Room } from 'livekit-client';
import type { ConnectionQuality, MediaParticipant, ParticipantRole } from './contract';
import { parseShareClaim, SHARE_CLAIM_ATTRIBUTE } from './shareArbiter';

const ROLES: readonly ParticipantRole[] = ['organizer', 'co_organizer', 'presenter', 'attendee'];

/** Attributes the client owns; hosts' decisions arrive on the same channel. */
export const HAND_ATTRIBUTE = 'handRaisedAt';
export const SPOTLIGHT_ATTRIBUTE = 'spotlighted';
export const FORCE_MUTE_ATTRIBUTE = 'forceMuted';
export const CAMERA_DISABLED_ATTRIBUTE = 'cameraDisabled';
export const SHARE_DISABLED_ATTRIBUTE = 'shareDisabled';
/**
 * Role CHANGES mid-meeting (promote to co-host, demote to attendee) and token
 * metadata cannot — it is signed once, at join. The server therefore writes the
 * live value to this attribute, and metadata is only the starting point.
 */
export const ROLE_ATTRIBUTE = 'role';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readText = (source: Record<string, unknown>, key: string): string | undefined => {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/** Token metadata is opaque to LiveKit; apps/api mints it as JSON. */
function parseMetadata(raw: string | undefined): Record<string, unknown> {
  if (raw === undefined || raw.length === 0) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const roleOf = (value: string | undefined, fallback: ParticipantRole): ParticipantRole =>
  ROLES.find((role) => role === value) ?? fallback;

export function toConnectionQuality(quality: LiveKitQuality): ConnectionQuality {
  switch (quality) {
    case LiveKitQuality.Excellent:
      return 'excellent';
    case LiveKitQuality.Poor:
      return 'poor';
    case LiveKitQuality.Lost:
      return 'lost';
    default:
      return 'good';
  }
}

export function toMediaParticipant(
  participant: Participant,
  isLocal: boolean,
  seed: MediaParticipant | undefined,
): MediaParticipant {
  const meta = parseMetadata(participant.metadata);
  const attributes = participant.attributes;
  const handRaisedAt = Number.parseInt(attributes[HAND_ATTRIBUTE] ?? '', 10);
  const avatarUrl = readText(meta, 'avatarUrl') ?? seed?.avatarUrl;

  return {
    id: participant.identity,
    userId: readText(meta, 'userId') ?? seed?.userId ?? participant.identity,
    name: participant.name ?? seed?.name ?? participant.identity,
    ...(avatarUrl ? { avatarUrl } : {}),
    // Attribute first (mutable, host-driven), then signed metadata, then seed.
    role: roleOf(
      attributes[ROLE_ATTRIBUTE] ?? readText(meta, 'role'),
      seed?.role ?? 'attendee',
    ),
    isLocal,
    cameraOn: participant.isCameraEnabled,
    micOn: participant.isMicrophoneEnabled,
    speaking: participant.isSpeaking,
    audioLevel: participant.audioLevel,
    handRaisedAt: Number.isFinite(handRaisedAt) ? handRaisedAt : null,
    sharing: participant.isScreenShareEnabled,
    shareClaimedAt: parseShareClaim(attributes[SHARE_CLAIM_ATTRIBUTE]),
    spotlighted: attributes[SPOTLIGHT_ATTRIBUTE] === '1',
    forceMuted: attributes[FORCE_MUTE_ATTRIBUTE] === '1',
    cameraDisabled: attributes[CAMERA_DISABLED_ATTRIBUTE] === '1',
    shareDisabled: attributes[SHARE_DISABLED_ATTRIBUTE] === '1',
    quality: toConnectionQuality(participant.connectionQuality),
    joinedAt: participant.joinedAt?.getTime() ?? seed?.joinedAt ?? Date.now(),
  };
}

/** The live roster. Ordering is stable; `orderTiles` does the layout ordering. */
export function rosterOf(
  room: Room,
  seedById: ReadonlyMap<string, MediaParticipant>,
): readonly MediaParticipant[] {
  const local = room.localParticipant;
  const remotes = [...room.remoteParticipants.values()].map((participant) =>
    toMediaParticipant(participant, false, seedById.get(participant.identity)),
  );
  return [toMediaParticipant(local, true, seedById.get(local.identity)), ...remotes];
}

/** Cheap change detector — stops a re-render on every audio-level tick. */
export const rosterSignature = (roster: readonly MediaParticipant[]): string =>
  roster
    .map((p) =>
      [
        p.id, p.name, p.role, p.cameraOn, p.micOn, p.speaking, p.sharing,
        /* The claim is in the signature because a claim CHANGE with no other
           roster change is exactly a take-over, and dropping that write would
           leave the loser publishing until something else moved. */
        p.shareClaimedAt,
        p.spotlighted, p.forceMuted, p.cameraDisabled, p.shareDisabled,
        p.handRaisedAt, p.quality,
      ].join('~'),
    )
    .join('|');
